/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ReactNode, useMemo, useRef } from 'react';
import {
  Table,
  TableColumn,
  Progress,
  Link,
  FavoriteToggle,
} from '@backstage/core-components';
import { useStarredEntities } from '@backstage/plugin-catalog-react';
import { Typography, makeStyles, useTheme } from '@material-ui/core';
import { getWarningPanel } from '../../helpers/getWarningPanel';
import { useAllScores } from '../../api/useAllScores';
import { EntityScoreExtended } from '../../api/types';
import { ScoreBar } from '../ScoreBar';
import { PartialScoreChip } from '../PartialScoreChip';
import { SetupChip } from '../SetupChip';
import { ScoreVerdict } from '../ScoreVerdict';
import { humanizeEntityName } from '../../helpers/humanizeEntityName';
import {
  DomainTitles,
  domainTitleOf,
  matchesSearch,
} from '../ScoreBoardPage/applyBoardScope';
import { useDomainTree } from '../ScoreBoardPage/useDomainTree';
import {
  BoardRow,
  flattenBoardRows,
  isGroupRow,
} from '../ScoreBoardPage/buildBoardRows';
import {
  DEFAULT_NAMESPACE,
  Entity,
  stringifyEntityRef,
} from '@backstage/catalog-model';

// The quality pillars, in fixed canonical order (feature 0012/0013;
// 'production', first named 'deployment', added by radar-backend-scorecard's
// metrics-tracker follow-up). Each is a
// breakdown column after the headline; a pillar measured on at least one
// system renders as a score bar, an unmeasured one as a muted "coming soon"
// column (caption in the header, faint dash in the cells). Columns auto-upgrade
// to real bars once their collector exists — no code change needed then.
export const PILLAR_COLUMNS: { key: string; title: string }[] = [
  { key: 'engineering', title: 'Engineering' },
  { key: 'product', title: 'Product' },
  { key: 'security', title: 'Security' },
  { key: 'delivery', title: 'Delivery' },
  // Was "Deployment"; renamed to "Production" (meeting 2026-10-20) for the
  // coming Prod tab with prod-bug metrics. Never measured, so no stored history.
  { key: 'production', title: 'Production' },
];

// The Total (Overall) is the row headline — a number-led verdict (feature 0019,
// ScoreVerdict), visually distinct from the bar-led pillar breakdown. The
// per-pillar breakdown bars are more compact. HEADLINE_NUMBER still sizes the
// muted "not scored" label on the headline.
const HEADLINE_NUMBER = '15px';
// The breakdown bar is fluid: it fills its pillar column (never a fixed width,
// which left most of a wide column empty), down to BREAKDOWN_BAR_MIN on a narrow
// screen. The number sits in a fixed slot after it, wide enough for "100 %" and
// "n/a", so bars end — and numbers start — at the same x on every row.
const BREAKDOWN_BAR_MIN = '3rem';
const BREAKDOWN_NUMBER_SLOT = '3rem';
const BREAKDOWN_BAR_HEIGHT = '12px';
const BREAKDOWN_NUMBER = '14px';

// The System name box: fixed (never content-sized) so the Overall verdict
// starts at the same x on every row, yet wide enough that real prod names up
// to ~30 characters ("CHM Підтримка товарного обліку", measured at 264px in
// the portal font, 1rem/700) and their Domain subtitle sit on one line; longer
// ones still wrap inside the box, at a space. The column is sized to hold star + name box + verdict
// (+ cell padding), so material-table splits only the REMAINING width among the
// pillar columns instead of giving every column an equal sixth.
export const SYSTEM_NAME_WIDTH = '17rem';
export const SYSTEM_COLUMN_WIDTH = '28rem';

// Every pillar column — measured or "coming soon" — gets an equal share of what
// the System column leaves. Explicit, because material-table's own default
// (`calc(20% - 0px)`: it cannot subtract a rem width) over-commits the row.
//
// The table is laid out `fixed`, so these widths are what the browser uses. In
// `auto` layout they were only hints: the browser resolved the over-commit by
// content (a column holding a chip came out wider than its neighbours), and
// material-table additionally pins each body cell to a px width measured once
// at mount, which disagrees with the header after any resize.
export const PILLAR_COLUMN_WIDTH = `calc((100% - ${SYSTEM_COLUMN_WIDTH}) / ${PILLAR_COLUMNS.length})`;
// The narrowest a pillar column may get: cell padding + bar minimum + number
// slot, and room for the longest header ("Engineering" + its sort arrow). A
// fixed layout never grows a column to fit its content, so the TABLE carries the
// floor — below it the table scrolls sideways inside its container.
const PILLAR_COLUMN_MIN = '9.5rem';
export const TABLE_MIN_WIDTH = `calc(${SYSTEM_COLUMN_WIDTH} + ${PILLAR_COLUMNS.length} * ${PILLAR_COLUMN_MIN})`;

// The leading chevron column of a board with subdomain rows (feature 0058, bug
// 0070). material-table (3.2.5) sizes the chevron's BODY cell
// `48 + 9 * (treeDataMaxLevel - 2)` px — `treeDataMaxLevel` being the deepest
// row's path length (top level = 1) — but renders its HEADER cell with no width
// at all. In a fixed layout the header row alone decides the columns, so that
// cell soaked up every pixel the explicit widths left over. The header gets the
// body cell's own formula, from the same depth, so every nesting level's
// indented chevron (9px a level) fits.
const treeToggleWidth = (depth: number): string =>
  `${48 + 9 * (Math.max(depth, 2) - 2)}px`;

/** The deepest row's level in the tree: a top-level row is 1. */
const treeDepthOf = (rows: BoardRow[], level = 1): number =>
  rows.reduce(
    (deepest, row) =>
      row.children?.length
        ? Math.max(deepest, treeDepthOf(row.children, level + 1))
        : deepest,
    level,
  );

// The same budget with the chevron column in it: the explicit widths must
// still add up to the table, or the leftover lands in some column again.
const pillarColumnWidthWithTree = (treeWidth: string) =>
  `calc((100% - ${SYSTEM_COLUMN_WIDTH} - ${treeWidth}) / ${PILLAR_COLUMNS.length})`;
const tableMinWidthWithTree = (treeWidth: string) =>
  `calc(${treeWidth} + ${SYSTEM_COLUMN_WIDTH} + ${PILLAR_COLUMNS.length} * ${PILLAR_COLUMN_MIN})`;

// The width FavoriteToggle's small IconButton occupies with zero padding —
// mirrored by the spacer a group row renders in its place.
const STAR_SLOT_WIDTH = '1.5rem';

const useStyles = makeStyles({
  root: {
    '& table': { minWidth: TABLE_MIN_WIDTH },
  },
  // Added only when the table is in tree mode, so a board without subdomains
  // (and the Score tab's embedded table) keeps exactly the layout above. A
  // stylesheet rule, because material-table offers no per-cell hook for the
  // tree header: `options.headerStyle` would reach every header, and a
  // replacement Header component would fork its sort/selection logic. The
  // cell's own inline style carries no width, so nothing outranks this one.
  withTree: {
    '& table': {
      minWidth: ({ treeWidth }: { treeWidth: string }) =>
        tableMinWidthWithTree(treeWidth),
    },
    '& thead th:first-child': {
      width: ({ treeWidth }: { treeWidth: string }) => treeWidth,
    },
  },
});

// Path to a System's Score tab. With a pillarKey it deep-links to that pillar
// (?pillar=); without one it points at the Score tab neutrally (the tab picks
// its own default pillar) — used by the "Overall" headline, which is not tied to
// any single pillar. A radar project with no catalog System has no Score tab:
// its card lives on the board.
export function scoreRoute(
  entityScore: EntityScoreExtended,
  pillarKey?: string,
): string {
  const ns = entityScore.entityRef.namespace ?? DEFAULT_NAMESPACE;
  const { kind, name } = entityScore.entityRef;
  if (entityScore.inCatalog === false) {
    // No Engineering/Security for such a project: open on Product.
    return `/score-board/project/${encodeURIComponent(name)}?pillar=${
      pillarKey ?? 'product'
    }`;
  }
  const base = `/catalog/${ns}/${kind}/${name}/score`;
  return pillarKey ? `${base}?pillar=${pillarKey}` : base;
}

const pillarOf = (entityScore: EntityScoreExtended, key: string) =>
  entityScore.pillars?.find(p => p.key === key);

/**
 * The pillar's own score, or `undefined` when it has none — the pillar is
 * unmeasured, missing from the row, or (IMC omits null properties) simply does
 * not carry the key. Returning the narrowed number means neither the sort nor
 * the cell has to re-assert that it exists.
 */
const measuredScoreOf = (
  entityScore: EntityScoreExtended,
  key: string,
): number | undefined => {
  const pillar = pillarOf(entityScore, key);
  return pillar?.measured && typeof pillar.scorePercent === 'number'
    ? pillar.scorePercent
    : undefined;
};

const isMeasured = (entityScore: EntityScoreExtended, key: string): boolean =>
  measuredScoreOf(entityScore, key) !== undefined;

type ScoreTableProps = {
  title?: string;
  /**
   * The rows, as a tree: a group row (a subdomain, feature 0058) carries its
   * members in `children`. A plain `EntityScoreExtended[]` is a valid tree of
   * depth one, which is what every consumer but the board passes.
   */
  scores: BoardRow[];
  /**
   * Whether the table owns its search box. The board passes `false` and
   * supplies its own — its search has to narrow the KPI strip too, and
   * material-table's search is private to the table (feature 0039). Every
   * other consumer keeps the built-in box.
   */
  searchable?: boolean;
  /**
   * Replaces material-table's toolbar row wholesale. Used by the board to put
   * its title and search where the table's own toolbar sits, rather
   * than growing a second header band above it.
   */
  toolbar?: ReactNode;
  /**
   * Domain display titles for the subtitle under each System name (feature
   * 0057). Absent or not yet loaded → the raw `spec.domain` is shown instead.
   */
  domainTitles?: DomainTitles;
  /**
   * Group rows (by `id`) to render already open — the board puts the groups
   * its search matched in here, so a hit on a member is visible without a
   * click (feature 0058). Not state: material-table owns the expansion from
   * the first render onward, and a reader's own clicks are never overridden.
   */
  expandedGroupIds?: ReadonlySet<string>;
};

export const ScoreTable = ({
  title,
  scores,
  searchable = true,
  toolbar,
  domainTitles,
  expandedGroupIds,
}: ScoreTableProps) => {
  const theme = useTheme();
  // The portal's own star, not a board-local list: starring here is the same
  // act as starring on the catalog page, and — with storage server-side
  // (ADR-0023) — it follows the person to their next device. Without a toggle
  // on the row, the board's Starred scope would be unusable on first run.
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();

  // The Toolbar override must keep ONE component identity for the life of the
  // table: material-table renders `components.Toolbar` as a component, so a
  // fresh arrow function each render remounts the whole toolbar — which would
  // steal focus from the board's search box after every single keystroke. The
  // node itself still changes freely; the stable component reads the latest one
  // through the ref.
  const toolbarRef = useRef(toolbar);
  toolbarRef.current = toolbar;
  const hasToolbar = toolbar !== undefined;
  const components = useMemo(
    () =>
      hasToolbar ? { Toolbar: () => <>{toolbarRef.current}</> } : undefined,
    [hasToolbar],
  );

  // A thin rule fences the "System / Overall" headline from the pillar
  // breakdown — it separates the headline from the breakdown group, it is NOT a
  // "5th pillar" divider (there is no standalone Total column anymore).
  const breakdownSeparator = `2px solid ${theme.palette.divider}`;

  // Every row of the tree, parents and members alike: a pillar measured only
  // inside a group must still get a real column, not a "coming soon" one.
  const allRows = flattenBoardRows(scores);
  // Tree mode is opt-in per render, not a permanent table shape:
  // material-table gives EVERY row a leading chevron cell once
  // `parentChildData` is set, so a board with no subdomains — and the Score
  // tab's own embedded table, which never has any — must not pay for it.
  const hasGroups = scores.some(isGroupRow);
  // One width for the chevron header, the pillar budget and the table floor.
  const treeWidth = treeToggleWidth(treeDepthOf(scores));
  const classes = useStyles({ treeWidth });
  const pillarColumnWidth = hasGroups
    ? pillarColumnWidthWithTree(treeWidth)
    : PILLAR_COLUMN_WIDTH;
  // A member row's caption names the group it sits in — looked up by its
  // `parentId`, not by `domain`: a nested group's `domain` is its TOP-LEVEL
  // ancestor, not the group row above it.
  const rowsById = new Map(allRows.map(row => [row.id, row]));
  const displayNameOf = (row: BoardRow) =>
    row.title ?? humanizeEntityName(row.entityRef.name);

  const columns: TableColumn<BoardRow>[] = [
    {
      // Leading headline: System name (catalog Title, humanised fallback) plus
      // the Total as the row's prominent, health-coloured score. No separate
      // Total column — this IS the overall score. Sorted by Total by default.
      title: 'System / Overall',
      field: 'scorePercent',
      defaultSort: 'desc',
      width: SYSTEM_COLUMN_WIDTH,
      // material-table matches a column by its `field`, which here is the
      // numeric Total — so without this the built-in search box could only ever
      // match a score, and a System name matched nothing (feature 0039). The
      // board supplies its own search; this keeps the embedded export honest,
      // matching on exactly the same rule.
      customFilterAndSearch: (filter, entityScore) =>
        matchesSearch(entityScore, filter, domainTitles),
      // A not-scored System (null Total, feature 0017) sorts as -1 so it sinks
      // to the bottom on the default desc sort rather than becoming NaN.
      customSort: (d1, d2) => (d1.scorePercent ?? -1) - (d2.scorePercent ?? -1),
      render: entityScore => {
        if (!entityScore.entityRef?.name) {
          return <>Missing entityRef.name key</>;
        }
        const displayName = displayNameOf(entityScore);
        // A group row is a subdomain, not a System: starring it would assert a
        // favourite nobody chose, and what it needs instead is the
        // denominator its mean was taken over (feature 0058). The chevron
        // itself is material-table's, rendered from `parentChildData`.
        const group = entityScore.group;
        // Null Total → the System is not scored (every repo unprofiled): show a
        // muted "not scored" label, never a 0%/red bar.
        const notScored =
          entityScore.scorePercent === null ||
          entityScore.scorePercent === undefined;
        const starred = isStarredEntity(entityScore.entityRef);
        const domainTitle = domainTitleOf(entityScore.domain, domainTitles);
        const parentRow =
          entityScore.parentId === undefined
            ? undefined
            : rowsById.get(entityScore.parentId);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
            {group ? (
              // A spacer, not nothing: the star's slot keeps every row's name
              // box — and therefore every row's Overall — starting at the same
              // x, group rows included.
              <span
                aria-hidden
                style={{
                  width: STAR_SLOT_WIDTH,
                  flexShrink: 0,
                  marginLeft: '-0.35rem',
                }}
              />
            ) : (
              <FavoriteToggle
                // The FULL ref, not the bare name: two Systems can share a name
                // across kinds or namespaces, and a duplicate id would point the
                // tooltip at the wrong row.
                id={`star-${stringifyEntityRef(entityScore.entityRef)}`}
                title={
                  starred
                    ? `Remove ${displayName} from your starred Systems`
                    : `Star ${displayName}`
                }
                isFavorite={starred}
                onToggle={() => toggleStarredEntity(entityScore.entityRef)}
                size="small"
                // The star is an affordance, not a column: it must not shift
                // the fixed-width name box that keeps every row's Overall
                // aligned.
                style={{ padding: 0, marginLeft: '-0.35rem' }}
              />
            )}
            <div
              style={{
                // Fixed width (not just minWidth) + break-word: a name longer
                // than the box wraps inside it (at a space; mid-word only for a
                // single word wider than the box) instead of pushing the
                // verdict right, so the Overall starts at the same x on every
                // row.
                width: SYSTEM_NAME_WIDTH,
                flexShrink: 0,
                overflowWrap: 'break-word',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '0.2rem',
              }}
            >
              <Link
                to={scoreRoute(entityScore)}
                data-id={entityScore.entityRef.name}
                style={{ fontSize: '1rem', fontWeight: 700 }}
              >
                {displayName}
              </Link>
              {/* Quiet under the name: for a member of a group row, the group
                  it sits in (bug 0070 — uncaptioned, members looked exactly
                  like the top-level Systems around them, so a reader could
                  not tell where the group ended); "in" sets it apart from a
                  Domain caption. For any other row, its business Domain
                  (feature 0057) — and nothing for a System without one. */}
              {parentRow ? (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  data-testid="member-of-group"
                  style={{ lineHeight: 1.2, marginTop: '-0.1rem' }}
                >
                  in {displayNameOf(parentRow)}
                </Typography>
              ) : null}
              {domainTitle && entityScore.parentId === undefined ? (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  data-testid="system-domain"
                  style={{ lineHeight: 1.2, marginTop: '-0.1rem' }}
                >
                  {domainTitle}
                </Typography>
              ) : null}
              {group ? (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  data-testid="group-member-count"
                  style={{ lineHeight: 1.2 }}
                >
                  {group.memberCount === 1
                    ? '1 system'
                    : `${group.memberCount} systems`}
                </Typography>
              ) : null}
              {entityScore.setup ? (
                <SetupChip setup={entityScore.setup} />
              ) : null}
            </div>
            {notScored ? (
              <span
                data-testid="overall-not-scored"
                style={{
                  fontSize: HEADLINE_NUMBER,
                  fontWeight: 700,
                  color: theme.palette.text.disabled,
                  fontStyle: 'italic',
                }}
              >
                not scored
              </span>
            ) : (
              <ScoreVerdict
                scorePercent={entityScore.scorePercent}
                scoreSuccess={entityScore.scoreSuccess}
                label="Overall"
              />
            )}
          </div>
        );
      },
    },
  ];

  // Breakdown columns: all four pillars in canonical order. Engineering (or any
  // measured pillar) → a score bar linking to its pillar-scoped Score tab;
  // Product / Security / Delivery (unmeasured) → a "coming soon" column.
  PILLAR_COLUMNS.forEach(({ key, title: pillarTitle }, idx) => {
    const measuredAnywhere = allRows.some(s => isMeasured(s, key));
    const isFirstBreakdown = idx === 0;
    const dividerStyle = isFirstBreakdown
      ? { borderLeft: breakdownSeparator, paddingLeft: '10px' }
      : undefined;

    if (measuredAnywhere) {
      columns.push({
        title: pillarTitle,
        field: `pillar_${key}`,
        sorting: true,
        width: pillarColumnWidth,
        headerStyle: dividerStyle,
        cellStyle: dividerStyle,
        // An unmeasured pillar sorts as -1 so it sinks to the bottom on a desc
        // sort rather than becoming NaN.
        customSort: (d1, d2) =>
          (measuredScoreOf(d1, key) ?? -1) - (measuredScoreOf(d2, key) ?? -1),
        render: entityScore => {
          const pillar = pillarOf(entityScore, key);
          const score = measuredScoreOf(entityScore, key);
          const measured = score !== undefined;
          const bar = (
            <ScoreBar
              scorePercent={score ?? null}
              scoreSuccess={pillar?.scoreSuccess}
              label={pillarTitle}
              height={BREAKDOWN_BAR_HEIGHT}
              numberFontSize={BREAKDOWN_NUMBER}
              numberWidth={BREAKDOWN_NUMBER_SLOT}
              trackMinWidth={BREAKDOWN_BAR_MIN}
            />
          );
          // `block`: an inline link would shrink the fluid bar to nothing.
          const cell = measured ? (
            <Link
              to={scoreRoute(entityScore, key)}
              style={{ display: 'block' }}
            >
              {bar}
            </Link>
          ) : (
            bar
          );
          // A pillar scored from only some of its areas is qualified in place
          // (feature 0037): the board has no areas and no banner, so without
          // this a code-only Security score reads as a complete one. Rendered
          // OUTSIDE the link, UNDER the bar (beside it, it widened the column),
          // and only when the qualifier exists — so only that row grows.
          return pillar?.partialReason ? (
            <div
              data-testid="pillar-cell-qualified"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: '0.2rem',
              }}
            >
              {cell}
              <div style={{ alignSelf: 'flex-start', lineHeight: 1 }}>
                <PartialScoreChip reason={pillar.partialReason} />
              </div>
            </div>
          ) : (
            cell
          );
        },
      });
    } else {
      // Unmeasured pillar: the "coming soon" caption lives in the header (said
      // once), the cells show a faint dash (not repeated text on every row).
      columns.push({
        title: (
          <div>
            <span>{pillarTitle}</span>
            <span
              style={{
                display: 'block',
                fontSize: '0.58rem',
                fontStyle: 'italic',
                letterSpacing: '0.03em',
                fontWeight: 400,
                color: theme.palette.text.disabled,
              }}
            >
              coming soon
            </span>
          </div>
        ),
        field: `pillar_${key}`,
        sorting: false,
        width: pillarColumnWidth,
        headerStyle: { ...dividerStyle, textAlign: 'center' },
        cellStyle: {
          ...dividerStyle,
          textAlign: 'center',
          color: theme.palette.text.disabled,
        },
        render: () => (
          <span
            data-testid={`pillar-soon-${key}`}
            aria-label={`${pillarTitle} not yet measured`}
          >
            —
          </span>
        ),
      });
    }
  });

  // in case we have less then 10 entities let's show at least 10 rows
  const minDefaultPageSizeOption = scores.length >= 10 ? scores.length : 10;
  // so in case we have less then 100 entities we want to see them all in one page after load
  const maxDefaultPageSizeOption =
    scores.length < 100 ? minDefaultPageSizeOption : 100;
  // so now we are in a situation, when
  // count(entities) | minDefaultPageSizeOption | maxDefaultPageSizeOption | defaultPageSizeOption
  //   0 |  10 |  10 |  10
  //   5 |  10 |  10 |  10
  //  10 |  10 |  10 |  10
  //  50 |  50 |  50 |  50
  // 100 | 100 | 100 | 100
  // 150 | 150 | 100 | 100
  const defaultPageSizeOption =
    minDefaultPageSizeOption > maxDefaultPageSizeOption
      ? maxDefaultPageSizeOption
      : minDefaultPageSizeOption;

  return (
    <div
      data-testid="score-board-table"
      className={
        hasGroups ? `${classes.root} ${classes.withTree}` : classes.root
      }
    >
      <Table<BoardRow>
        title={title ?? 'Systems'}
        components={components}
        options={{
          search: searchable,
          // Two-state sorting: a header click toggles asc ↔ desc. Without this
          // material-table's third click drops the sort (orderBy -1) and the
          // rows fall back to their unsorted input order. Every ScoreTable —
          // the board and the entity embedding — renders through here.
          thirdSortClick: false,
          // Column widths are enforced, not content-negotiated (see
          // PILLAR_COLUMN_WIDTH): every pillar column is the same width.
          tableLayout: 'fixed',
          paging: true,
          padding: 'dense',
          pageSize: defaultPageSizeOption,
          pageSizeOptions: [defaultPageSizeOption, 20, 50, 100, 200],
          // Groups the board's search matched open on the first render; every
          // other group starts collapsed (feature 0058).
          defaultExpanded: (row: BoardRow) =>
            expandedGroupIds?.has(row.id) ?? false,
        }}
        columns={columns}
        // Children stay REAL rows — the four pillar columns line up with their
        // parent's and every renderer above is reused untouched, which a
        // detail panel would have had to redraw.
        parentChildData={
          hasGroups
            ? (row, rows) =>
                row.parentId === undefined
                  ? undefined
                  : rows.find(candidate => candidate.id === row.parentId)
            : undefined
        }
        data={allRows}
      />
    </div>
  );
};

type ScoreCardTableProps = {
  title?: string;
  entityKindFilter?: string[];
  entity?: Entity;
};
export const ScoreCardTable = ({
  title,
  entityKindFilter,
  entity,
}: ScoreCardTableProps) => {
  const {
    loading,
    error,
    value: data,
  } = useAllScores(entityKindFilter, entity);
  const domainTitles = useDomainTree().titles;

  if (loading) {
    return <Progress />;
  } else if (error) {
    return getWarningPanel(error);
  }

  return (
    <ScoreTable title={title} scores={data || []} domainTitles={domainTitles} />
  );
};
